import { API_BASE_URL } from '../lib/api-config';
import {
    getStoreValue, setStoreValue, nowStamp, today, logActivity, nextNumericId, nextPrefixedId
} from './superAdmin/superAdminStore.js';
import {
    USE_STATIC_DATA, STORE_KEYS, getPlans, planWithBoards, planPrice, orgSubscription,
    activatePlan, createInvoice, updateInvoice, deleteInvoice, getInvoices, createPaymentOrder, findPayment,
    getKits, reviewKit, getStaticSession, setStaticSession, clearStaticSession,
    INITIAL_SUBSCRIPTION_HISTORY
} from './superAdmin/staticData.js';



// Pure-JS SHA-256 fallback for when crypto.subtle isn't available (it only
// exists in a "secure context" — HTTPS or localhost; a plain http://<lan-ip>
// origin has no crypto.subtle at all). Must stay byte-for-byte identical to
// the copy in superAdmin/paymentFlow.js — both hash the same passwords and
// have to agree on every input.
const sha256Hex = (str) => {
    const rightRotate = (value, amount) => (value >>> amount) | (value << (32 - amount));
    const K = [
        0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5, 0x3956c25b, 0x59f111f1, 0x923f82a4, 0xab1c5ed5,
        0xd807aa98, 0x12835b01, 0x243185be, 0x550c7dc3, 0x72be5d74, 0x80deb1fe, 0x9bdc06a7, 0xc19bf174,
        0xe49b69c1, 0xefbe4786, 0x0fc19dc6, 0x240ca1cc, 0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da,
        0x983e5152, 0xa831c66d, 0xb00327c8, 0xbf597fc7, 0xc6e00bf3, 0xd5a79147, 0x06ca6351, 0x14292967,
        0x27b70a85, 0x2e1b2138, 0x4d2c6dfc, 0x53380d13, 0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85,
        0xa2bfe8a1, 0xa81a664b, 0xc24b8b70, 0xc76c51a3, 0xd192e819, 0xd6990624, 0xf40e3585, 0x106aa070,
        0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5, 0x391c0cb3, 0x4ed8aa4a, 0x5b9cca4f, 0x682e6ff3,
        0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208, 0x90befffa, 0xa4506ceb, 0xbef9a3f7, 0xc67178f2
    ];
    let h0 = 0x6a09e667, h1 = 0xbb67ae85, h2 = 0x3c6ef372, h3 = 0xa54ff53a;
    let h4 = 0x510e527f, h5 = 0x9b05688c, h6 = 0x1f83d9ab, h7 = 0x5be0cd19;

    const utf8 = unescape(encodeURIComponent(str));
    const bytes = [];
    for (let i = 0; i < utf8.length; i++) bytes.push(utf8.charCodeAt(i));
    const bitLength = bytes.length * 8;
    bytes.push(0x80);
    while (bytes.length % 64 !== 56) bytes.push(0);
    for (let shift = 24; shift >= 0; shift -= 8) bytes.push(0); // high 32 bits of length (always 0 here)
    for (let shift = 24; shift >= 0; shift -= 8) bytes.push((bitLength >>> shift) & 0xff);

    for (let chunkStart = 0; chunkStart < bytes.length; chunkStart += 64) {
        const w = new Array(64);
        for (let i = 0; i < 16; i++) {
            const j = chunkStart + i * 4;
            w[i] = ((bytes[j] << 24) | (bytes[j + 1] << 16) | (bytes[j + 2] << 8) | bytes[j + 3]) >>> 0;
        }
        for (let i = 16; i < 64; i++) {
            const s0 = rightRotate(w[i - 15], 7) ^ rightRotate(w[i - 15], 18) ^ (w[i - 15] >>> 3);
            const s1 = rightRotate(w[i - 2], 17) ^ rightRotate(w[i - 2], 19) ^ (w[i - 2] >>> 10);
            w[i] = (w[i - 16] + s0 + w[i - 7] + s1) >>> 0;
        }

        let a = h0, b = h1, c = h2, d = h3, e = h4, f = h5, g = h6, h = h7;
        for (let i = 0; i < 64; i++) {
            const S1 = rightRotate(e, 6) ^ rightRotate(e, 11) ^ rightRotate(e, 25);
            const ch = (e & f) ^ (~e & g);
            const temp1 = (h + S1 + ch + K[i] + w[i]) >>> 0;
            const S0 = rightRotate(a, 2) ^ rightRotate(a, 13) ^ rightRotate(a, 22);
            const maj = (a & b) ^ (a & c) ^ (b & c);
            const temp2 = (S0 + maj) >>> 0;
            h = g; g = f; f = e; e = (d + temp1) >>> 0;
            d = c; c = b; b = a; a = (temp1 + temp2) >>> 0;
        }

        h0 = (h0 + a) >>> 0; h1 = (h1 + b) >>> 0; h2 = (h2 + c) >>> 0; h3 = (h3 + d) >>> 0;
        h4 = (h4 + e) >>> 0; h5 = (h5 + f) >>> 0; h6 = (h6 + g) >>> 0; h7 = (h7 + h) >>> 0;
    }

    const toHex = n => (n >>> 0).toString(16).padStart(8, "0");
    return [h0, h1, h2, h3, h4, h5, h6, h7].map(toHex).join("");
};

const hashPassword = async (plain) => {
    if (window.crypto && window.crypto.subtle) {
        const buf = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(plain));
        return Array.from(new Uint8Array(buf)).map(b => b.toString(16).padStart(2, "0")).join("");
    }
    return sha256Hex(plain);
};


const PASS = {};
const RECORD_PROFILE = {};
let installed = false;
let seeds = null;

const loadSeeds = () => {
    if (!seeds) {
        seeds = Promise.all([
            import('./superAdmin/Organizations.jsx'),
            import('./superAdmin/UserList.jsx')
        ]).then(([orgModule, userModule]) => ({
            organizations: orgModule.INITIAL_ORGANIZATIONS,
            users: userModule.STATIC_USERS
        }));
    }
    return seeds;
};

const respond = (body, status = 200) => ({
    ok: status < 400,
    status,
    json: () => Promise.resolve(body),
    text: () => Promise.resolve(JSON.stringify(body))
});

const ok = (data, message) => ({ status: 'success', ...(message ? { message } : {}), ...(data !== undefined ? { data } : {}) });
const fail = message => ({ status: 'error', message });

const paginate = (list, params) => {
    const limit = Number(params.get('limit')) || list.length || 1;
    const offset = Number(params.get('offset')) || 0;
    return { rows: list.slice(offset, offset + limit), total: list.length };
};

/* ------------------------------------------------------------------ helpers */

const userTypeOf = u => u.user_type || (/student/i.test(u.role || '') ? 'student' : (/dealer/i.test(u.role || '') ? 'dealer' : 'customer'));
const roleLabel = type => ({ student: 'Student', dealer: 'Dealer' }[type] || 'Customer');

const splitName = u => {
    if (u.f_name !== undefined) return { f_name: u.f_name, l_name: u.l_name || '' };
    const parts = String(u.name || '').trim().split(/\s+/);
    return { f_name: parts[0] || '', l_name: parts.slice(1).join(' ') };
};

const normalizeUser = u => ({
    id: u.id,
    ...splitName(u),
    email: u.email,
    mobile_no: u.mobile_no || '',
    user_type: userTypeOf(u),
    status: u.status || 'active',
    created_at: u.created_at || `${u.joinedDate || today()} 10:00:00`,
    // boards this user has received (delivered board orders + sample purchases)
    purchases: u.purchases || [],
    boards_count: (u.purchases || []).reduce((s, p) => s + Number(p.quantity || 0), 0)
});


const orgRow = o => ({
    id: o.id,
    institute_name: o.name,
    contact_email: o.email,
    contact_phone: o.phone,
    address: o.address,
    operational_tier: (orgSubscription(o.id) || {}).plan_name || o.type,
    created_at: `${o.joinedDate} 00:00:00`
});

const planName = id => {
    const plan = getPlans().find(p => p.id === Number(id));
    return plan ? plan.plan_name : null;
};

/* ------------------------------------------------------------------ handlers */

const handle = async (params, body, method) => {
    const action = params.get('action');
    const sub = params.get('sub_action');
    const session = getStaticSession();

    // ---------- auth: handled by the real API ----------
    if (action === 'logout') {
        clearStaticSession();
        return PASS;
    }

    if (action === 'login') {
        const email = String(body.email || '').trim().toLowerCase();
        const password = String(body.password || '');
        if (email && password) {
            const { organizations: orgSeed } = await loadSeeds();
            const org = getStoreValue('organizations', orgSeed)
                .find(o => String(o.email || '').trim().toLowerCase() === email && o.adminPasswordHash);
            if (org) {
                const hash = await hashPassword(password);
                if (hash !== org.adminPasswordHash) return fail('Incorrect password.');
                setStaticSession({
                    role: 'admin',
                    orgId: org.id,
                    profile: {
                        id: org.id, email: org.email, user_type: 'admin',
                        // Topbar.jsx renders `${profile.f_name} ${profile.l_name}` — without
                        // these it showed "undefined undefined" for these org-admin accounts.
                        f_name: org.name, l_name: 'Admin',
                        name: org.name, institute_id: org.id, institute_name: org.name
                    },
                    logged_in_at: nowStamp(),
                    local: true
                });
                return { status: 'success', user_type: 'admin' };
            }
        }
        clearStaticSession();
        return PASS;
    }

    if (action === 'profile') {
        const localSession = getStaticSession();
        if (localSession && localSession.local) return ok(localSession.profile);
        return RECORD_PROFILE;
    }

    if (!session) return PASS;

    const { organizations: orgSeed, users: userSeed } = await loadSeeds();
    const getOrgs = () => getStoreValue('organizations', orgSeed);
    const setOrgs = list => setStoreValue('organizations', list);
    const getIndividuals = () => getStoreValue('users', userSeed);
    const org = session.role === 'admin' ? getOrgs().find(o => o.id === session.orgId) : null;
    const orgUsers = org ? (org.users || []) : [];

    // ---------- admin overview ----------
    if (action === 'admin_action' && params.get('admin_action') === 'overview') {
        if (!org) return fail(`No organization in Super Admin → Organizations uses the email ${session.profile.email}.`);
        const users = orgUsers.map(normalizeUser);
        const active = users.filter(u => u.status === 'active').length;
        const count = type => users.filter(u => u.user_type === type).length;

        const purchases = orgUsers.reduce((all, u) => all.concat(u.purchases || []), []);
        const byModel = {};
        const byMonth = {};
        purchases.forEach(p => {
            byModel[p.boardName] = (byModel[p.boardName] || 0) + Number(p.quantity || 0);
            const ym = String(p.purchaseDate || '').slice(0, 7);
            if (ym) byMonth[ym] = (byMonth[ym] || 0) + Number(p.quantity || 0);
        });

        const days = Number(params.get('days')) || 7;
        const signups = [];
        for (let i = days - 1; i >= 0; i--) {
            const d = new Date(`${today()}T00:00:00`);
            d.setDate(d.getDate() - i);
            const date = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
            const dayUsers = users.filter(u => u.created_at.slice(0, 10) === date);
            signups.push({
                date,
                customers: dayUsers.filter(u => u.user_type !== 'student').length,
                students: dayUsers.filter(u => u.user_type === 'student').length
            });
        }

        const subscription = orgSubscription(org.id);
        // the user limit set by the Super Admin (Organizations) wins over the plan's limit
        const usersLimit = Number(org.userLimit) > 0 ? Number(org.userLimit) : (subscription ? subscription.users_limit : null);
        return ok({
            end_users: {
                total: users.length, active, inactive: users.length - active,
                customers: count('customer'), students: count('student'), dealers: count('dealer')
            },
            kits: {
                total: purchases.reduce((s, p) => s + Number(p.quantity || 0), 0),
                by_model: Object.keys(byModel).map(model_no => ({ model_no, total: byModel[model_no] })),
                over_time: Object.keys(byMonth).sort().map(ym => ({ ym, total: byMonth[ym] }))
            },
            daily_signups: signups,
            subscription: subscription ? {
                plan_name: subscription.plan_name,
                users_limit: usersLimit,
                total_days: subscription.total_days,
                used_days: subscription.used_days,
                remaining_days: subscription.remaining_days
            } : null
        });
    }

    // ---------- admin: manage users ----------
    if (action === 'admin_action' && params.get('admin_action') === 'view-users') {
        const q = String(params.get('search') || '').toLowerCase();
        const orderBy = params.get('order_by') || 'created_at';
        const dir = (params.get('sort_dir') || 'DESC').toUpperCase() === 'ASC' ? 1 : -1;
        const rows = orgUsers.map(normalizeUser)
            .filter(u => !q || `${u.f_name} ${u.l_name} ${u.email}`.toLowerCase().includes(q))
            .sort((a, b) => String(a[orderBy] || '').localeCompare(String(b[orderBy] || '')) * dir);
        return ok(paginate(rows, params));
    }

    // ---------- institutions / registrations ----------
    if (action === 'manage_institutions') {
        if (sub === 'institutions') {
            const list = org ? [org] : getOrgs();
            return ok(paginate(list.map(orgRow), params));
        }
        if (sub === 'individuals') {
            const list = org ? orgUsers.map(normalizeUser) : getIndividuals();
            return ok(paginate(list, params));
        }
        if (sub === 'register_individual') {
            const email = String(body.email || '').trim().toLowerCase();
            if (!body.f_name || !email) return fail('First name and email are required.');
            const taken = getOrgs().some(o => (o.users || []).some(u => String(u.email).toLowerCase() === email)) ||
                getIndividuals().some(u => String(u.email).toLowerCase() === email);
            if (taken) return fail('A user with this email already exists.');

            // an organization admin always creates users inside their own organization
            const instituteId = org ? org.id : (body.institute_id ? Number(body.institute_id) : null);
            const type = body.user_type || 'customer';
            if (instituteId) {
                const target = getOrgs().find(o => o.id === instituteId);
                if (!target) return fail('Institution not found.');
                const userCount = (target.users || []).length;
                if (Number(target.userLimit) > 0) {
                    // user limit set by the Super Admin while creating / editing the organization
                    if (userCount >= Number(target.userLimit)) {
                        return fail(`User limit reached: ${target.name} can have at most ${target.userLimit} users. Ask the Super Admin to increase the limit.`);
                    }
                } else {
                    const subscription = orgSubscription(target.id);
                    if (subscription && userCount >= subscription.users_limit) {
                        return fail(`User limit reached: the ${subscription.plan_name} plan allows ${subscription.users_limit} users. Upgrade your subscription to add more users.`);
                    }
                }
                const allUsers = getOrgs().reduce((all, o) => all.concat(o.users || []), []);
                const newUser = {
                    id: nextNumericId(allUsers),
                    name: `${body.f_name} ${body.l_name || ''}`.trim(),
                    f_name: body.f_name,
                    l_name: body.l_name || '',
                    role: roleLabel(type),
                    user_type: type,
                    email: body.email.trim(),
                    mobile_no: body.mobile_no || '',
                    status: 'active',
                    joinedDate: today(),
                    created_at: nowStamp(),
                    purchases: []
                };
                setOrgs(getOrgs().map(o => (o.id === target.id ? { ...o, users: [...(o.users || []), newUser] } : o)));
                logActivity('Registered new User', `${newUser.name} → ${target.name}`, 'Users');
            } else {
                const individuals = getIndividuals();
                const newUser = {
                    id: nextPrefixedId(individuals, 'USR'),
                    f_name: body.f_name,
                    l_name: body.l_name || '',
                    email: body.email.trim(),
                    mobile_no: body.mobile_no ? `+91 ${String(body.mobile_no).replace(/^\+91\s*/, '')}` : '',
                    status: 'active',
                    created_at: nowStamp(),
                    location: '',
                    last_login: 'Never',
                    user_type: type,
                    purchased_boards: []
                };
                setStoreValue('users', [newUser, ...individuals]);
                logActivity('Registered new User', `${newUser.f_name} ${newUser.l_name} (${newUser.id})`, 'Users');
            }
            return ok(undefined, 'User registered successfully.');
        }
        if (sub === 'register') {
            const email = String(body.contact_email || '').trim();
            if (!body.institute_name || !email) return fail('Institution name and email are required.');
            if (getOrgs().some(o => String(o.email).toLowerCase() === email.toLowerCase())) {
                return fail('An organization with this email already exists.');
            }
            const orgs = getOrgs();
            const newOrg = {
                id: nextNumericId(orgs),
                name: body.institute_name.trim(),
                type: 'School',
                email,
                phone: body.mobile_no ? `+91 ${body.mobile_no}` : '',
                address: body.address || '',
                contact_person: body.contact_person || '',
                joinedDate: today(),
                users: []
            };
            setOrgs([newOrg, ...orgs]);
            const tierPlan = getPlans().find(p => p.plan_name.toLowerCase() === String(body.operational_tier || '').toLowerCase());
            if (tierPlan) activatePlan({ targetId: newOrg.id, planId: tierPlan.id, cycle: 'monthly', startsAt: body.activation_date || today(), endsAt: body.expiry_date || undefined });
            logActivity('Registered new Organization', newOrg.name, 'Organizations');
            return ok({ id: newOrg.id }, 'Institution registered. The Super Admin can create its admin login in Organizations (Edit → Admin Password).');
        }
        return PASS;
    }

    // ---------- subscription plans (the Super Admin edits the same list) ----------
    if (action === 'subscription_plans' && sub === 'list') {
        const plans = [...getPlans()].sort((a, b) => (a.sort_order || a.id) - (b.sort_order || b.id));
        return ok(plans.map(p => ({ ...planWithBoards(p), is_popular: p.is_popular ? 1 : 0, is_best_value: p.is_best_value ? 1 : 0 })));
    }

    // ---------- subscription history ----------
    if (action === 'subscription_history') {
        if (sub === 'list') {
            const history = getStoreValue(STORE_KEYS.history, INITIAL_SUBSCRIPTION_HISTORY)
                .filter(h => h.target_type === 'institute' && org && h.institute_id === org.id)
                .map(h => ({ ...h, old_plan_name: h.old_plan_id ? planName(h.old_plan_id) : null, new_plan_name: planName(h.new_plan_id) }));
            return ok(paginate(history, params));
        }
        if (sub === 'change') {
            activatePlan({
                targetType: body.target_type === 'individual' ? 'individual' : 'institute',
                targetId: Number(body.target_id),
                planId: Number(body.plan_id),
                startsAt: body.starts_at || today(),
                endsAt: body.ends_at || undefined
            });
            return ok(undefined, 'Plan updated.');
        }
    }

    // ---------- invoices ----------
    if (action === 'invoices') {
        const userIds = orgUsers.map(u => u.id);
        const mine = i => !org || i.institute_id === org.id || (i.user_id && userIds.includes(i.user_id));
        if (sub === 'list') {
            const status = params.get('status');
            const rows = getInvoices()
                .filter(i => mine(i) && (!status || i.status === status))
                .sort((a, b) => b.id - a.id)
                .map(i => {
                    const user = i.user_id ? orgUsers.find(u => u.id === i.user_id) : null;
                    const inst = i.institute_id ? getOrgs().find(o => o.id === i.institute_id) : null;
                    return {
                        ...i,
                        institute_name: inst ? inst.name : null,
                        ...(user ? splitName(user) : {}),
                        plan_name: i.plan_id ? planName(i.plan_id) : null
                    };
                });
            return ok(paginate(rows, params));
        }
        if (sub === 'create') {
            const invoice = createInvoice({
                instituteId: body.institute_id ? Number(body.institute_id) : null,
                userId: body.user_id ? Number(body.user_id) : null,
                planId: body.plan_id ? Number(body.plan_id) : null,
                amount: body.amount,
                cycle: body.billing_cycle,
                dueDate: body.due_date || null
            });
            logActivity('Created Invoice', `${invoice.invoice_no} — ₹${invoice.amount.toLocaleString('en-IN')}`, 'Payments');
            return ok({ id: invoice.id }, 'Invoice created.');
        }
        if (sub === 'update_status') {
            updateInvoice(Number(body.id), { status: body.status });
            return ok(undefined, 'Status updated.');
        }
        if (sub === 'delete') {
            deleteInvoice(Number(body.id));
            return ok(undefined, 'Invoice deleted.');
        }
    }

    // ---------- payments (mock Cashfree) ----------
    if (action === 'payment') {
        if (sub === 'create_order') {
            if (!org) return fail('Only an organization admin can subscribe.');
            const plan = getPlans().find(p => p.id === Number(body.plan_id));
            if (!plan) return fail('Plan not found.');
            const cycle = body.billing_cycle === 'annual' ? 'annual' : 'monthly';
            const payment = createPaymentOrder({ org, plan, cycle });
            return ok({
                env: 'static',
                cf_order_id: payment.cf_order_id,
                payment_session_id: payment.cf_payment_session_id,
                amount: planPrice(plan, cycle)
            });
        }
        if (sub === 'status') {
            const payment = findPayment(params.get('cf_order_id'));
            if (!payment) return fail('Order not found.');
            return ok({ status: payment.status, cf_order_id: payment.cf_order_id });
        }
    }

    // ---------- board approvals ----------
    if (action === 'kit_management') {
        if (sub === 'list_review') {
            const q = String(params.get('search') || '').toLowerCase();
            const status = params.get('status');
            const rows = getKits()
                .filter(k => (!status || k.status === status) &&
                    (!q || `${k.kit_name} ${k.serial_code} ${k.model_no}`.toLowerCase().includes(q)))
                .sort((a, b) => b.id - a.id);
            return ok(paginate(rows, params));
        }
        if (sub === 'approve') {
            reviewKit(Number(body.id), 'approved');
            return ok(undefined, 'Board approved.');
        }
        if (sub === 'reject') {
            reviewKit(Number(body.id), 'rejected', body.reason);
            return ok(undefined, 'Board rejected.');
        }
    }

    return PASS;
};

// Remembers who is logged in (from the real API's profile) and which organization they belong to.
// An organization admin is matched to the organization whose email is their login email.
const rememberApiUser = async data => {
    if (!data || data.status !== 'success' || !data.data) {
        clearStaticSession();
        return;
    }
    const user = data.data;
    let org = null;
    if (user.user_type === 'admin') {
        const { organizations } = await loadSeeds();
        const email = String(user.email || '').trim().toLowerCase();
        org = getStoreValue('organizations', organizations)
            .find(o => String(o.email || '').trim().toLowerCase() === email) || null;
    }
    setStaticSession({
        role: user.user_type,
        orgId: org ? org.id : null,
        profile: { ...user, institute_id: org ? org.id : null, institute_name: org ? org.name : null },
        logged_in_at: nowStamp()
    });
};

export const installStaticApi = () => {
    if (!USE_STATIC_DATA || installed || typeof window === 'undefined' || typeof window.fetch !== 'function') return;
    installed = true;
    const realFetch = window.fetch.bind(window);

    window.fetch = (input, init = {}) => {
        const url = typeof input === 'string' ? input : (input && input.url) || '';
        if (url.indexOf(API_BASE_URL) !== 0) return realFetch(input, init);

        const params = new URLSearchParams(url.split('?')[1] || '');
        let body = {};
        try {
            body = init.body ? JSON.parse(init.body) : {};
        } catch (e) {
            body = {};
        }

        return handle(params, body, (init.method || 'GET').toUpperCase()).then(result => {
            if (result === PASS) return realFetch(input, init);
            if (result === RECORD_PROFILE) {
                // real profile request; remember the user before the page continues
                return realFetch(input, init).then(res => res.clone().json()
                    .then(rememberApiUser)
                    .catch(() => clearStaticSession())
                    .then(() => res));
            }
            if (Array.isArray(result)) return respond(result[0], result[1]);
            return respond(result);
        });
    };
};
